/** Voice-note capture coordinated with the app-wide wake-word controller. */
import * as React from 'react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/solid';
import { useVoiceNote } from '@/hooks/useVoiceNote';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/utils';

interface VoiceCaptureProps {
  onComplete?: () => void;
  autoStartKey?: number;
}

export function VoiceCapture({ onComplete, autoStartKey = 0 }: VoiceCaptureProps): React.ReactElement {
  const note = useVoiceNote({ onResult: () => onComplete?.() });
  const { isRecording, isUploading, startRecording } = note;
  const wakeEnabled = useUIStore((state) => state.wakeWordEnabled);
  const wakeSessionActivated = useUIStore((state) => state.wakeWordSessionActivated);
  const wakeListening = useUIStore((state) => state.wakeWordListening);
  const wakeStatus = useUIStore((state) => state.wakeWordStatus);
  const setWakeEnabled = useUIStore((state) => state.setWakeWordEnabled);
  const activateWakeSession = useUIStore((state) => state.activateWakeWordSession);
  const setVoiceCaptureBusy = useUIStore((state) => state.setVoiceCaptureBusy);
  const handledAutoStartRef = React.useRef(0);

  const autoStartPending = Boolean(
    autoStartKey && handledAutoStartRef.current !== autoStartKey && !isRecording && !isUploading,
  );

  React.useEffect(() => {
    setVoiceCaptureBusy(isRecording || isUploading || autoStartPending);
    return () => setVoiceCaptureBusy(false);
  }, [autoStartPending, isRecording, isUploading, setVoiceCaptureBusy]);

  React.useEffect(() => {
    if (!autoStartKey || handledAutoStartRef.current === autoStartKey || isRecording || isUploading) return;
    handledAutoStartRef.current = autoStartKey;
    const handoff = window.setTimeout(() => void startRecording(), 240);
    return () => window.clearTimeout(handoff);
  }, [autoStartKey, isRecording, isUploading, startRecording]);

  if (!note.isSupported) {
    return <p className="py-6 text-center text-sm text-text-secondary">Voice recording isn’t supported in this browser.</p>;
  }

  const status = note.isUploading
    ? 'Transcribing your note…'
    : note.isRecording
      ? 'Recording… tap to stop'
      : 'Tap the mic to record a voice note';

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-3 py-4">
        <button
          type="button"
          onClick={() => (note.isRecording ? note.stopRecording() : void note.startRecording())}
          disabled={note.isUploading}
          aria-label={note.isRecording ? 'Stop recording' : 'Start recording'}
          className={cn(
            'flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg transition focus:outline-none focus:ring-4 focus:ring-primary/25 active:scale-[0.97]',
            note.isRecording ? 'animate-pulse bg-error motion-reduce:animate-none' : 'bg-primary hover:brightness-110',
            note.isUploading && 'opacity-50',
          )}
        >
          {note.isRecording ? <StopIcon className="h-8 w-8" /> : <MicrophoneIcon className="h-8 w-8" />}
        </button>
        <p className="text-sm text-text-secondary">{status}</p>
        {note.error ? <p className="text-sm text-error">{note.error}</p> : null}
      </div>

      {wakeStatus !== 'unsupported' ? (
        <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-outline-variant/40 bg-surface-container-low p-3 text-sm">
          <span>
            <span className="block font-medium text-text-primary">Hands-free “hey brain”</span>
            <span className="mt-1 block text-xs leading-5 text-text-muted">
              Listens across the app while this tab is open. It stops when the app is minimized or your phone is locked.
            </span>
          </span>
          <input
            type="checkbox"
            checked={wakeEnabled}
            onChange={() => {
              if (wakeEnabled && !wakeSessionActivated) activateWakeSession();
              else setWakeEnabled(!wakeEnabled);
            }}
            className="mt-1 h-4 w-4 shrink-0 accent-primary"
          />
        </label>
      ) : null}
      {wakeEnabled ? (
        <p className="text-center text-xs text-text-muted">
          {!wakeSessionActivated
            ? 'Hands-free listening is enabled. Use the checkbox above to resume it for this browser session.'
            : wakeListening
              ? 'Listening for “hey brain”…'
            : wakeStatus === 'paused'
              ? 'Paused while this recording uses the microphone.'
              : 'Starting microphone…'}
        </p>
      ) : null}
    </div>
  );
}
